import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from '@/_utils/axios';
import { FocusVersionScheduling, PolarisApiResponse } from '@/redux/slices/slice_types';
import AvailScheduleDetailsBreadcrumbs from '@/components/Focus/AvailsScheduleDetails/AvailScheduleDetailsBreadcrumbs';
import { Box, Container, Grid, Paper, Skeleton as SkeletonMUI, Stack, styled, Typography } from '@mui/material';
import FocusImage from '@/components/Focus/common/FocusImage';
import FocusImageContainer from '@/components/Focus/common/FocusImageContainer';
import FocusTitleHierarchyBadge from '@/components/Focus/common/FocusTitleHierarchyBadge';
import { LicenseChip } from '@/components/_common/Table/Calendar/CalendarComponents/common';
import VersionNameChip from '@/components/Focus/common/VersionNameChip';
import { format } from 'date-fns';
import { useAppSelector } from '@/redux/hooks';
import { selectUserPreferences } from '@/redux/selectors/authSelectors';
import PolarisIcon from '@/components/_common/PolarisIcon';
import AvailEnumMap from '@/components/Focus/Avails/AvailsEnumMap';
import { selectAvailPlatform } from '@/redux/selectors/constantsSelectors';
import ScheduleDetailsTabs from '@/components/Focus/AvailsScheduleDetails/ScheduleDetailsTabs';
import { Spinner } from '@/components/_common';
import PlatformIcon from '@/components/_common/Table/Calendar/CalendarSharedComponents/PlatformIcon';
import { useFocusTheme } from '@/components/Focus/configs/useFocusTheme.hook';
import { type FocusTheme } from '@/components/Focus/configs/theme';
import { useToast } from '@/components/_common/Toast';
import { updateSchedule } from '@/components/Focus/utils/utils';

const dataCellIconProps = {
  width: 32,
  height: 32,
  style: {
    padding: '4px'
  }
};

const AvailsScheduleDetails: React.FC = () => {
  const { id } = useParams<{ id: string | undefined }>();
  const theme = useFocusTheme();
  const { date_format, time_format } = useAppSelector(selectUserPreferences);
  const allPlatforms = useAppSelector(selectAvailPlatform);
  const [scheduleDetails, setScheduleDetails] = useState<FocusVersionScheduling | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { error } = useToast();

  // TODO: Replace the API call to get details of the version scheduling details, not scheduling
  const loadScheduleDetails = useCallback(() => {
    setScheduleDetails(null);
    setIsLoading(true);
    axios
      .post<PolarisApiResponse<FocusVersionScheduling>>(`/focus/scheduling/${id}/`, { baseURL: '/api/v1.1' })
      .then((res) => {
        setScheduleDetails(res.data.data);
      })
      .catch(({ data }) => {
        error(data?.message ?? 'Failed to load schedule details');
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [id]);

  const saveScheduleDetails = useCallback((schedule: Partial<FocusVersionScheduling>) => {
    if (!id) return;

    setIsLoading(true);

    return updateSchedule(Number(id), schedule)
      .then((res) => {
        setScheduleDetails(res.data.data);
      })
      .catch(({ data }) => {
        error(data?.message ?? 'Failed to save schedule');
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  useEffect(() => {
    loadScheduleDetails();
  }, [loadScheduleDetails]);

  const seasonOrder = scheduleDetails?.title.season_sequence_order;
  const episodeOrder = scheduleDetails?.title.episode_sequence_order;
  const videoElement = scheduleDetails?.elements.find((el) => el.element.element_type === 'video')?.element;

  return (
    <>
      <AvailScheduleDetailsBreadcrumbs
        isLoading={isLoading}
        displayedItemTitle={scheduleDetails?.title.client_ids_supplemental.alpha_id}
        loadScheduleDetails={loadScheduleDetails}
      />
      <Wrapper>
        <Grid container spacing={1.5} pt={1.5} pb={3} sx={{ mt: 0 }}>
          <Grid item xs={12} md={6} container alignItems="stretch">
            <InfoTile>
              <Grid container gap={3} flexGrow={1}>
                <Grid item width={210}>
                  {scheduleDetails ? (
                    <FocusImageContainer sx={{ my: -0.25 }}>
                      <FocusImage entity={scheduleDetails} />
                    </FocusImageContainer>
                  ) : (
                    <Skeleton height="100%" />
                  )}
                </Grid>
                <Grid item flexGrow={1}>
                  <Stack direction="row" flexWrap="wrap" gap={1.5}>
                    <Box mb={1} flexGrow={1}>
                      {scheduleDetails ? (
                        !!seasonOrder && !!episodeOrder && <FocusTitleHierarchyBadge seasonOrder={seasonOrder} episodeOrder={episodeOrder} />
                      ) : (
                        <Skeleton width={200} height={32} sx={{ mb: 1 }} />
                      )}
                    </Box>
                    {scheduleDetails ? (
                      <>
                        <LicenseChip label={scheduleDetails.license_type.toLocaleUpperCase()} variant="outlined" />
                        <VersionNameChip label={scheduleDetails.title.availed_type} />
                      </>
                    ) : (
                      <>
                        <Skeleton width={80} height={32} />
                        <Skeleton width={80} height={32} />
                      </>
                    )}
                  </Stack>

                  {scheduleDetails ? (
                    <>
                      <Typography sx={{ mb: 1, fontSize: '1.643rem', lineHeight: '2.143rem', fontWeight: 700 }}>
                        {scheduleDetails.title.title_name}
                      </Typography>
                      <Typography sx={{ fontSize: '1.143rem', lineHeight: '1.285rem', fontWeight: 700, color: theme.palette.neutral[20] }}>
                        {scheduleDetails.title.title_name_supplemental}
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Skeleton width={250} height={30} sx={{ mb: 1 }} />
                      <Skeleton width={350} height={18} />
                    </>
                  )}
                </Grid>
              </Grid>
            </InfoTile>
          </Grid>
          <Grid item xs={12} md={6} container alignItems="stretch">
            <InfoTile>
              <Grid container spacing={0.5}>
                {scheduleDetails ? (
                  <>
                    <DataCell label="Alpha ID" icon={<AlphaIdIcon />}>
                      {scheduleDetails.title.client_ids_supplemental.alpha_id}
                    </DataCell>
                    <DataCell label="Platform" icon={<PlatformIcon name={scheduleDetails.availed_platform} />}>
                      {allPlatforms[scheduleDetails.availed_platform]}
                    </DataCell>
                    {videoElement && (
                      <DataCell label="AV Language">
                        <AvailEnumMap type="language" value={videoElement.language} />
                      </DataCell>
                    )}
                    <DataCell label="Term Start" icon={<PolarisIcon icon="calendar" {...dataCellIconProps} />}>
                      {date_format ? format(new Date(scheduleDetails.term_start), date_format) : null}
                      {time_format ? `, ${format(new Date(scheduleDetails.term_start), time_format)}` : null}
                    </DataCell>
                    <DataCell label="Term End" icon={<PolarisIcon icon="calendar" {...dataCellIconProps} />}>
                      {date_format ? format(new Date(scheduleDetails.term_end), date_format) : null}
                      {time_format ? `, ${format(new Date(scheduleDetails.term_end), time_format)}` : null}
                    </DataCell>
                    {videoElement?.video_resolution_type && (
                      <DataCell label="Format">
                        <Box sx={{ textTransform: 'uppercase' }}>{videoElement.video_resolution_type}</Box>
                      </DataCell>
                    )}
                  </>
                ) : (
                  <>
                    {Array.from({ length: 6 }).map((_, i) => (
                      <Grid key={i} item xs={4}>
                        <Skeleton height={55} />
                      </Grid>
                    ))}
                  </>
                )}
              </Grid>
            </InfoTile>
          </Grid>
        </Grid>

        {!scheduleDetails || isLoading ? (
          <Box position="relative" minHeight="300px">
            <Spinner />
          </Box>
        ) : (
          <ScheduleDetailsTabs schedule={scheduleDetails} saveSchedule={saveScheduleDetails} />
        )}
      </Wrapper>
    </>
  );
};

export default AvailsScheduleDetails;

const Wrapper = styled(Container)`
  background: ${({ theme }) => (theme as FocusTheme).palette.neutral[80]};
  min-height: calc(100vh - 255px);
  border-radius: 0 0 ${({ theme }) => theme.shape.borderRadius * 2.4 + 'px'} ${({ theme }) => theme.shape.borderRadius * 2.4 + 'px'};
  padding-bottom: ${({ theme }) => theme.spacing(3)};
`;

const InfoTile = styled(Stack)`
  flex-grow: 1;
  padding: ${({ theme }) => theme.spacing(2)};
  border-radius: ${({ theme }) => theme.shape.borderRadius * 2.4 + 'px'};
  background: ${({ theme }) => (theme as FocusTheme).palette.neutral[90]};
`;

const Skeleton = styled(SkeletonMUI)`
  transform: none;
`;

const DataCell: React.FC<{
  label: string;
  children: React.ReactNode;
  icon?: React.ReactNode;
}> = ({ label, children, icon }) => {
  const theme = useFocusTheme();

  return (
    <Grid item alignItems="stretch" container xs={4}>
      <StyledPaper sx={{ px: 1.5, py: 1, flexGrow: 1 }}>
        <Stack direction="row" alignItems="center">
          <Box flexGrow={1}>
            <Typography
              sx={{ mb: 0.5, fontSize: '0.857rem', lineHeight: '1.214rem', fontWeight: 600, color: theme.customOverrides.Calendar.textSecondary }}
            >
              {label}
            </Typography>
            <Box sx={{ fontSize: '1.143rem', lineHeight: '1.286rem' }}>{children}</Box>
          </Box>
          <Box display="flex" alignItems="center">
            {icon}
          </Box>
        </Stack>
      </StyledPaper>
    </Grid>
  );
};

export const StyledPaper = styled(Paper, { shouldForwardProp: (propKey: string) => !['big'].includes(propKey) })<{ big?: boolean }>`
  background-color: ${({ theme }) => theme.palette.background.paper};
`;

const AlphaIdIcon = () => {
  return (
    <AlphaIdIconWrapper>
      <PolarisIcon icon="progress_star" {...dataCellIconProps} />
    </AlphaIdIconWrapper>
  );
};

const AlphaIdIconWrapper = styled(Box)`
  display: flex;
  align-items: center;
  border-radius: 50%;
  background-color: ${({ theme }) => (theme as FocusTheme).customOverrides.Calendar.backgroundDefault};
  color: ${({ theme }) => theme.palette.tertiary.main};
`;
